import { useState, useEffect, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { StarRating } from '../components/StarRating';
import { ReferralSection } from '../components/ReferralSection';
import { PropertiesSection } from '../components/PropertySection';
import { AgentProfileEditor } from '../components/AgentProfileEditor';
import { PlanningManager } from '../components/PlanningManager';
import { ShareProfileDialog } from '../components/ShareProfileDialog';
const SellerPropertyManager = lazy(() => import('../components/SellerPropertyManager').then(m => ({ default: m.SellerPropertyManager })));
const SellerListings = lazy(() => import('../components/SellerListings').then(m => ({ default: m.SellerListings })));
const MessagingPanel = lazy(() => import('../components/MessagingPanel').then(m => ({ default: m.MessagingPanel })));
const AnalyticsDashboard = lazy(() => import('../components/AnalyticsDashboard').then(m => ({ default: m.AnalyticsDashboard })));
import {
    LayoutDashboard,
    Calendar as CalendarIcon,
    Clock,
    User,
    Star,
    Award,
    Plus,
    Trash2,
    CheckCircle,
    XCircle,
    Loader2,
    Settings,
    TrendingUp,
    Gift,
    Home,
    MessageSquare,
    BarChart3,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardPage = () => {
    const navigate = useNavigate();
    const { user, token, updateUser } = useAuth();

    const [appointments, setAppointments] = useState([]);
    const [availabilities, setAvailabilities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    const [contactProperty, setContactProperty] = useState(null);

    // Availability creation
    const [showAddSlot, setShowAddSlot] = useState(false);
    const [newSlotDate, setNewSlotDate] = useState(null);
    const [newSlotStart, setNewSlotStart] = useState('09:00');
    const [newSlotEnd, setNewSlotEnd] = useState('10:00');
    const [addingSlot, setAddingSlot] = useState(false);

    // Review modal
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [reviewAppointment, setReviewAppointment] = useState(null);
    const [reviewRating, setReviewRating] = useState(5);
    const [reviewComment, setReviewComment] = useState('');
    const [submittingReview, setSubmittingReview] = useState(false);

    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }
        fetchData();
    }, [user]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [appointmentsRes] = await Promise.all([
                axios.get(`${API}/appointments/my`, {
                    headers: { Authorization: `Bearer ${token}` },
                }),
            ]);
            setAppointments(appointmentsRes.data);

            if (user.user_type === 'agent') {
                const availRes = await axios.get(`${API}/availabilities/my`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setAvailabilities(availRes.data);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const addAvailability = async () => {
        if (!newSlotDate) {
            toast.error('Sélectionnez une date');
            return;
        }

        setAddingSlot(true);
        try {
            await axios.post(
                `${API}/availabilities`,
                {
                    date: format(newSlotDate, 'yyyy-MM-dd'),
                    start_time: newSlotStart,
                    end_time: newSlotEnd,
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            toast.success('Créneau ajouté');
            setShowAddSlot(false);
            setNewSlotDate(null);
            fetchData();
        } catch (error) {
            toast.error('Erreur lors de l\'ajout du créneau');
        } finally {
            setAddingSlot(false);
        }
    };

    const deleteAvailability = async (id) => {
        try {
            await axios.delete(`${API}/availabilities/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            toast.success('Créneau supprimé');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };

    const updateAppointmentStatus = async (appointmentId, status) => {
        try {
            await axios.put(
                `${API}/appointments/${appointmentId}/status?status=${status}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(`Rendez-vous ${status === 'confirmed' ? 'confirmé' : status === 'cancelled' ? 'annulé' : 'terminé'}`);
            fetchData();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const submitReview = async () => {
        if (!reviewAppointment) return;

        setSubmittingReview(true);
        try {
            await axios.post(
                `${API}/reviews`,
                {
                    agent_id: reviewAppointment.agent_id,
                    appointment_id: reviewAppointment.id,
                    rating: reviewRating,
                    comment: reviewComment,
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            toast.success('Merci pour votre avis !');
            setShowReviewModal(false);
            setReviewAppointment(null);
            setReviewRating(5);
            setReviewComment('');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        } finally {
            setSubmittingReview(false);
        }
    };

    const pendingAppointments = appointments.filter((a) => a.status === 'pending');
    const confirmedAppointments = appointments.filter((a) => a.status === 'confirmed');
    const completedAppointments = appointments.filter((a) => a.status === 'completed');

    const timeOptions = [];
    for (let h = 8; h <= 19; h++) {
        timeOptions.push(`${h.toString().padStart(2, '0')}:00`);
        timeOptions.push(`${h.toString().padStart(2, '0')}:30`);
    }

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-scalimmo-navy" />
            </div>
        );
    }

    return (
        <div className="min-h-screen pt-24 pb-16 bg-gray-50/50" data-testid="dashboard-page">
            <div className="container-scalimmo">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                    <div>
                        <h1 className="font-heading text-3xl font-semibold text-scalimmo-navy">
                            Bonjour, {user?.first_name}
                        </h1>
                        <p className="text-muted-foreground">
                            {user?.user_type === 'agent' ? 'Gérez vos rendez-vous et disponibilités' : 'Suivez vos rendez-vous'}
                        </p>
                    </div>

                    {user?.user_type === 'agent' && (
                        <div className="flex items-center gap-3 flex-wrap">
                            {user?.is_top_performer && (
                                <Badge className="elite-badge">
                                    <Award className="w-4 h-4" />
                                    Top Performer
                                </Badge>
                            )}
                            <ShareProfileDialog user={user} profileViews={user?.profile_views || 0} />
                            <Dialog open={showAddSlot} onOpenChange={setShowAddSlot}>
                                <DialogTrigger asChild>
                                    <Button className="btn-primary" data-testid="add-availability">
                                        <Plus className="w-4 h-4 mr-2" />
                                        Ajouter un créneau
                                    </Button>
                                </DialogTrigger>
                                <DialogContent>
                                    <DialogHeader>
                                        <DialogTitle className="font-heading">Nouveau créneau</DialogTitle>
                                        <DialogDescription>
                                            Ajoutez une disponibilité à votre calendrier
                                        </DialogDescription>
                                    </DialogHeader>
                                    <div className="space-y-4 py-4">
                                        <Calendar
                                            mode="single"
                                            selected={newSlotDate}
                                            onSelect={setNewSlotDate}
                                            disabled={(date) => date < new Date()}
                                            className="rounded-md border"
                                            locale={fr}
                                        />

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <Label>Début</Label>
                                                <Select value={newSlotStart} onValueChange={setNewSlotStart}>
                                                    <SelectTrigger>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {timeOptions.map((t) => (
                                                            <SelectItem key={t} value={t}>
                                                                {t}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div>
                                                <Label>Fin</Label>
                                                <Select value={newSlotEnd} onValueChange={setNewSlotEnd}>
                                                    <SelectTrigger>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {timeOptions.map((t) => (
                                                            <SelectItem key={t} value={t}>
                                                                {t}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>

                                        <Button
                                            className="w-full btn-primary"
                                            onClick={addAvailability}
                                            disabled={addingSlot}
                                            data-testid="confirm-add-slot"
                                        >
                                            {addingSlot ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Ajouter'}
                                        </Button>
                                    </div>
                                </DialogContent>
                            </Dialog>
                        </div>
                    )}
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                    <div className="bg-white rounded-xl p-6 border border-border/60 shadow-soft">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                <CalendarIcon className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-heading font-semibold text-scalimmo-navy">
                                    {appointments.length}
                                </p>
                                <p className="text-sm text-muted-foreground">Total RDV</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl p-6 border border-border/60 shadow-soft">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                                <Clock className="w-5 h-5 text-yellow-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-heading font-semibold text-scalimmo-navy">
                                    {pendingAppointments.length}
                                </p>
                                <p className="text-sm text-muted-foreground">En attente</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl p-6 border border-border/60 shadow-soft">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                <CheckCircle className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-heading font-semibold text-scalimmo-navy">
                                    {confirmedAppointments.length}
                                </p>
                                <p className="text-sm text-muted-foreground">Confirmés</p>
                            </div>
                        </div>
                    </div>

                    {user?.user_type === 'agent' && (
                        <div className="bg-white rounded-xl p-6 border border-border/60 shadow-soft">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-scalimmo-gold/20 rounded-lg flex items-center justify-center">
                                    <Star className="w-5 h-5 text-scalimmo-gold" />
                                </div>
                                <div>
                                    <p className="text-2xl font-heading font-semibold text-scalimmo-navy">
                                        {(user?.avg_rating || 0).toFixed(1)}
                                    </p>
                                    <p className="text-sm text-muted-foreground">Note moyenne</p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="mb-6">
                        <TabsTrigger value="overview" className="flex items-center gap-2">
                            <LayoutDashboard className="w-4 h-4" />
                            Vue d'ensemble
                        </TabsTrigger>
                        {user?.user_type === 'agent' && (
                            <>
                                <TabsTrigger value="properties" className="flex items-center gap-2">
                                    <Home className="w-4 h-4" />
                                    Mes Biens
                                </TabsTrigger>
                                <TabsTrigger value="seller-listings" className="flex items-center gap-2">
                                    <Home className="w-4 h-4" />
                                    Biens à vendre
                                </TabsTrigger>
                                <TabsTrigger value="calendar" className="flex items-center gap-2">
                                    <CalendarIcon className="w-4 h-4" />
                                    Disponibilités
                                </TabsTrigger>
                                <TabsTrigger value="profile" className="flex items-center gap-2">
                                    <User className="w-4 h-4" />
                                    Mon Profil
                                </TabsTrigger>
                                <TabsTrigger value="referral" className="flex items-center gap-2">
                                    <Gift className="w-4 h-4" />
                                    Parrainage
                                </TabsTrigger>
                                <TabsTrigger value="analytics" className="flex items-center gap-2">
                                    <BarChart3 className="w-4 h-4" />
                                    Statistiques
                                </TabsTrigger>
                            </>
                        )}
                        {user?.user_type === 'seller' && (
                            <TabsTrigger value="my-properties" className="flex items-center gap-2">
                                <Home className="w-4 h-4" />
                                Mes Biens
                            </TabsTrigger>
                        )}
                        <TabsTrigger value="messages" className="flex items-center gap-2">
                            <MessageSquare className="w-4 h-4" />
                            Messages
                        </TabsTrigger>
                    </TabsList>

                    {/* Overview Tab */}
                    <TabsContent value="overview">
                        <div className="space-y-6">
                            {/* Pending */}
                            {pendingAppointments.length > 0 && (
                                <div className="bg-white rounded-xl border border-border/60 p-6 shadow-soft">
                                    <h2 className="font-heading text-xl font-semibold text-scalimmo-navy mb-4">
                                        En attente de confirmation ({pendingAppointments.length})
                                    </h2>
                                    <div className="space-y-4">
                                        {pendingAppointments.map((apt) => (
                                            <AppointmentCard
                                                key={apt.id}
                                                appointment={apt}
                                                userType={user.user_type}
                                                onConfirm={() => updateAppointmentStatus(apt.id, 'confirmed')}
                                                onCancel={() => updateAppointmentStatus(apt.id, 'cancelled')}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Confirmed */}
                            {confirmedAppointments.length > 0 && (
                                <div className="bg-white rounded-xl border border-border/60 p-6 shadow-soft">
                                    <h2 className="font-heading text-xl font-semibold text-scalimmo-navy mb-4">
                                        Rendez-vous confirmés ({confirmedAppointments.length})
                                    </h2>
                                    <div className="space-y-4">
                                        {confirmedAppointments.map((apt) => (
                                            <AppointmentCard
                                                key={apt.id}
                                                appointment={apt}
                                                userType={user.user_type}
                                                onComplete={() => updateAppointmentStatus(apt.id, 'completed')}
                                                onCancel={() => updateAppointmentStatus(apt.id, 'cancelled')}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Completed */}
                            {completedAppointments.length > 0 && (
                                <div className="bg-white rounded-xl border border-border/60 p-6 shadow-soft">
                                    <h2 className="font-heading text-xl font-semibold text-scalimmo-navy mb-4">
                                        Rendez-vous terminés ({completedAppointments.length})
                                    </h2>
                                    <div className="space-y-4">
                                        {completedAppointments.map((apt) => (
                                            <AppointmentCard
                                                key={apt.id}
                                                appointment={apt}
                                                userType={user.user_type}
                                                onReview={
                                                    user.user_type === 'seller'
                                                        ? () => {
                                                              setReviewAppointment(apt);
                                                              setShowReviewModal(true);
                                                          }
                                                        : null
                                                }
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {appointments.length === 0 && (
                                <div className="bg-white rounded-xl border border-border/60 p-12 shadow-soft text-center">
                                    <CalendarIcon className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                                    <h3 className="font-heading text-xl font-semibold text-scalimmo-navy mb-2">
                                        Aucun rendez-vous
                                    </h3>
                                    <p className="text-muted-foreground mb-6">
                                        {user?.user_type === 'seller'
                                            ? 'Trouvez un agent et prenez rendez-vous'
                                            : 'Ajoutez des créneaux pour recevoir des réservations'}
                                    </p>
                                    <Button
                                        className="btn-primary"
                                        onClick={() =>
                                            navigate(user?.user_type === 'seller' ? '/agents' : '#')
                                        }
                                    >
                                        {user?.user_type === 'seller' ? 'Trouver un agent' : 'Ajouter des créneaux'}
                                    </Button>
                                </div>
                            )}
                        </div>
                    </TabsContent>

                    {/* Calendar Tab (Agent only) */}
                    {user?.user_type === 'agent' && (
                        <TabsContent value="calendar">
                            <PlanningManager />
                        </TabsContent>
                    )}

                    {/* Properties Tab (Agent only) */}
                    {user?.user_type === 'agent' && (
                        <TabsContent value="properties">
                            <PropertiesSection />
                        </TabsContent>
                    )}

                    {/* Profile Tab (Agent only) */}
                    {user?.user_type === 'agent' && (
                        <TabsContent value="profile">
                            <AgentProfileEditor />
                        </TabsContent>
                    )}

                    {/* Referral Tab (Agent only) */}
                    {user?.user_type === 'agent' && (
                        <TabsContent value="referral">
                            <ReferralSection />
                        </TabsContent>
                    )}

                    {/* Analytics Tab (Agent only) */}
                    {user?.user_type === 'agent' && (
                        <TabsContent value="analytics">
                            <Suspense fallback={<div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-scalimmo-navy" /></div>}>
                                <AnalyticsDashboard />
                            </Suspense>
                        </TabsContent>
                    )}

                    {/* Seller Listings Tab (Agent only) */}
                    {user?.user_type === 'agent' && (
                        <TabsContent value="seller-listings">
                            <Suspense fallback={<div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-scalimmo-navy" /></div>}>
                                <SellerListings onContactSeller={(prop) => {
                                    setContactProperty(prop);
                                    setActiveTab('messages');
                                }} />
                            </Suspense>
                        </TabsContent>
                    )}

                    {/* Seller Properties Tab (Seller only) */}
                    {user?.user_type === 'seller' && (
                        <TabsContent value="my-properties">
                            <Suspense fallback={<div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-scalimmo-navy" /></div>}>
                                <SellerPropertyManager />
                            </Suspense>
                        </TabsContent>
                    )}

                    {/* Messages Tab (Both) */}
                    <TabsContent value="messages">
                        <Suspense fallback={<div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-scalimmo-navy" /></div>}>
                            <MessagingPanel initialContact={contactProperty} />
                        </Suspense>
                    </TabsContent>
                </Tabs>
            </div>

            {/* Review Modal */}
            <Dialog open={showReviewModal} onOpenChange={setShowReviewModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading">Laisser un avis</DialogTitle>
                        <DialogDescription>
                            Partagez votre expérience avec{' '}
                            {reviewAppointment?.agent?.first_name} {reviewAppointment?.agent?.last_name}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="flex justify-center">
                            <StarRating rating={reviewRating} onRate={setReviewRating} size="lg" />
                        </div>
                        <textarea
                            placeholder="Décrivez votre expérience..."
                            value={reviewComment}
                            onChange={(e) => setReviewComment(e.target.value)}
                            rows={4}
                            className="w-full rounded-lg border border-input bg-transparent px-3 py-2 text-sm"
                            data-testid="review-comment"
                        />
                        <Button
                            className="w-full btn-primary"
                            onClick={submitReview}
                            disabled={submittingReview}
                            data-testid="submit-review"
                        >
                            {submittingReview ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Envoyer mon avis'}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

const AppointmentCard = ({ appointment, userType, onConfirm, onCancel, onComplete, onReview }) => {
    const other = userType === 'agent' ? appointment.seller : appointment.agent;

    return (
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 rounded-lg border border-border bg-gray-50/50">
            <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-scalimmo-navy/10 flex items-center justify-center">
                    <User className="w-6 h-6 text-scalimmo-navy" />
                </div>
                <div>
                    <p className="font-medium text-scalimmo-navy">
                        {other?.first_name} {other?.last_name}
                    </p>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                            <CalendarIcon className="w-3.5 h-3.5" />
                            {appointment.date}
                        </span>
                        <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {appointment.start_time} - {appointment.end_time}
                        </span>
                    </div>
                    {appointment.notes && (
                        <p className="text-sm text-muted-foreground mt-1 italic">"{appointment.notes}"</p>
                    )}
                </div>
            </div>

            <div className="flex items-center gap-2">
                {appointment.status === 'pending' && userType === 'agent' && (
                    <>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={onCancel}
                            className="text-destructive border-destructive/30 hover:bg-destructive/10"
                        >
                            <XCircle className="w-4 h-4 mr-1" />
                            Refuser
                        </Button>
                        <Button size="sm" onClick={onConfirm} className="bg-green-600 hover:bg-green-700">
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Confirmer
                        </Button>
                    </>
                )}

                {appointment.status === 'confirmed' && userType === 'agent' && (
                    <Button size="sm" onClick={onComplete} className="btn-primary">
                        Marquer terminé
                    </Button>
                )}

                {appointment.status === 'completed' && userType === 'seller' && onReview && (
                    <Button size="sm" onClick={onReview} className="btn-gold">
                        <Star className="w-4 h-4 mr-1" />
                        Laisser un avis
                    </Button>
                )}

                {appointment.status === 'pending' && userType === 'seller' && (
                    <Badge variant="secondary" className="bg-yellow-100 text-yellow-700">
                        En attente
                    </Badge>
                )}
            </div>
        </div>
    );
};

export default DashboardPage;
